// React
import { useState } from "react";

// Third-party
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  BadgeCheck,
  Banknote,
  Building,
  Calendar,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  HeartHandshake,
  Home,
  Map,
  MapPin,
  Search,
  Shield,
  Sparkles,
  Users,
} from "lucide-react";

// Hooks & Contexts
import { useTranslation } from "@/hooks";
import { useAuth } from "@/contexts";

// Components
import { CustomSelect } from "@/components/CustomSelect";
import { Header, HEADER_HEIGHT } from "@/components/Header";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

// Constants
import {
  AppRoutes,
  OCCUPANT_OPTIONS,
  ROOM_LAYOUTS,
  TOKYO_WARDS,
} from "@/constants";

export function HomePage() {
  const t = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Search form state
  const [prefecture, setPrefecture] = useState("tokyo");
  const [city, setCity] = useState("tokyo");
  const [location, setLocation] = useState("");
  const [moveInDate, setMoveInDate] = useState<Date | null>(null);
  const [occupants, setOccupants] = useState(1);
  const [rentMax, setRentMax] = useState<number>(200000);
  const [roomLayout, setRoomLayout] = useState<string[]>([]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    // Build URL params from search form
    const params = new URLSearchParams();

    // Add location params (always include prefecture at minimum)
    if (prefecture)
      params.set("prefecture", prefecture === "tokyo" ? "Tokyo" : prefecture);
    if (city) params.set("city", city === "tokyo" ? "Tokyo" : city);
    if (location) params.set("ward", location);

    // Add occupants if more than 1 (affects room size recommendations)
    if (occupants > 1) params.set("occupants", String(occupants));

    // Add move-in date if specified
    if (moveInDate) {
      const dateStr = moveInDate.toISOString().split("T")[0];
      if (dateStr) params.set("moveInDate", dateStr);
    }

    // Add rent budget (only if less than max)
    if (rentMax < 500000) {
      params.set("rentMax", String(rentMax));
    }

    // Add room layout if specified
    if (roomLayout.length > 0) {
      params.set("roomLayout", roomLayout.join(","));
    }

    // Navigate to overview page with filters
    const queryString = params.toString();
    const overviewPath = AppRoutes.overview || "/overview";
    navigate(queryString ? `${overviewPath}?${queryString}` : overviewPath);
  };

  const today = new Date();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large shape - top right */}
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-red-200/30 rounded-3xl animate-blob-smooth blur-3xl" />
        {/* Medium shape - bottom left */}
        <div
          className="absolute -bottom-32 -left-32 w-80 h-80 bg-red-200/30 rounded-3xl animate-blob-smooth blur-3xl"
          style={{ animationDelay: "2s" }}
        />
        {/* Small shape - middle */}
        <div
          className="absolute top-1/3 left-1/4 w-48 h-48 bg-red-300/25 rounded-full animate-blob-smooth blur-3xl"
          style={{ animationDelay: "4s" }}
        />
        {/* Extra shape - top left */}
        <div
          className="absolute top-20 -left-16 w-64 h-64 bg-red-200/25 rounded-full animate-blob-smooth blur-2xl"
          style={{ animationDelay: "1s" }}
        />
        {/* Another shape - bottom right */}
        <div
          className="absolute bottom-20 right-10 w-56 h-56 bg-red-200/30 rounded-full animate-blob-smooth blur-3xl"
          style={{ animationDelay: "3s" }}
        />
      </div>

      {/* Header with scroll-aware transparency */}
      <Header
        position="fixed"
        variant="scroll-aware"
        showShadow
        rightContent={
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {user ? (
              <Link
                to={AppRoutes.dashboard}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {t("dashboard")}
              </Link>
            ) : (
              <Link
                to={AppRoutes.login}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {t("signIn")}
              </Link>
            )}
          </div>
        }
      />

      {/* Hero Section - with padding for fixed header */}
      <main
        className="pt-24 pb-16 relative z-10"
        style={{ paddingTop: HEADER_HEIGHT + 24 }}
      >
        <div className="max-w-7xl 2xl:max-w-[1600px] 3xl:max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12">
          {/* Hero Content */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              {t("homeHeroTitle")}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t("homeHeroSubtitle")}
            </p>
          </div>

          {/* Search Card */}
          <div className="max-w-5xl mx-auto">
            <form
              onSubmit={handleSearch}
              className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8"
            >
              {/* First Row: Prefecture, City, Location */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                {/* Prefecture */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("searchPrefecture")}
                  </label>
                  <CustomSelect
                    value={prefecture}
                    onChange={(val) => setPrefecture(String(val))}
                    options={[{ value: "tokyo", label: "Tokyo" }]}
                    placeholder={t("searchSelectPrefecture")}
                    icon={<Map className="w-5 h-5 text-gray-400" />}
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("searchCity")}
                  </label>
                  <CustomSelect
                    value={city}
                    onChange={(val) => setCity(String(val))}
                    options={[{ value: "tokyo", label: "Tokyo" }]}
                    placeholder={t("searchSelectCity")}
                    icon={<Building className="w-5 h-5 text-gray-400" />}
                  />
                </div>

                {/* Location (Ward) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("searchLocation")}
                  </label>
                  <CustomSelect
                    value={location}
                    onChange={(val) => setLocation(String(val))}
                    options={[
                      { value: "", label: t("searchSelectArea") },
                      ...TOKYO_WARDS.map((ward) => ({
                        value: ward.nameEn,
                        label: ward.nameEn,
                      })),
                    ]}
                    placeholder={t("searchSelectArea")}
                    icon={<MapPin className="w-5 h-5 text-gray-400" />}
                  />
                </div>
              </div>

              {/* Second Row: Move-in Date, Monthly Budget, Occupants (3 cols) */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                {/* Move-in Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("searchMoveIn")}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Calendar className="w-5 h-5 text-gray-400" />
                    </div>
                    <DatePicker
                      selected={moveInDate}
                      onChange={(date: Date | null) => setMoveInDate(date)}
                      minDate={today}
                      placeholderText={t("searchMoveInPlaceholder")}
                      dateFormat="MMM d, yyyy"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg hover:border-gray-400 cursor-pointer"
                      calendarClassName="apaato-calendar"
                    />
                  </div>
                </div>

                {/* Monthly Rent Budget */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("searchRentBudget")}
                  </label>
                  <div className="relative flex items-center">
                    <div className="absolute left-0 pl-3 flex items-center pointer-events-none">
                      <Banknote className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={rentMax.toLocaleString()}
                      onChange={(e) => {
                        const num = Number(
                          e.target.value.replace(/[^0-9]/g, ""),
                        );
                        if (!isNaN(num)) setRentMax(Math.min(num, 500000));
                      }}
                      className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg hover:border-gray-400 focus:border-red-500 outline-none transition-all duration-200 shadow-none focus:shadow-[inset_0_0_0_1px_#ef4444]"
                      placeholder="200,000"
                    />
                    <div className="absolute right-0 inset-y-0 flex items-center">
                      <span className="text-gray-500 text-sm mr-1">¥/mo</span>
                      <div className="flex flex-col border-l border-gray-300 h-full">
                        <button
                          type="button"
                          onClick={() =>
                            setRentMax(Math.min(rentMax + 10000, 500000))
                          }
                          className="flex-1 px-2 hover:bg-gray-100 flex items-center justify-center border-b border-gray-300 rounded-tr-lg transition-colors"
                        >
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setRentMax(Math.max(rentMax - 10000, 0))
                          }
                          className="flex-1 px-2 hover:bg-gray-100 flex items-center justify-center rounded-br-lg transition-colors"
                        >
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Occupants */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("searchOccupants")}
                  </label>
                  <CustomSelect
                    value={occupants}
                    onChange={(val) => setOccupants(Number(val))}
                    options={OCCUPANT_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label:
                        opt.value === 5
                          ? `${opt.label} ${t("searchPeople")}`
                          : `${opt.label} ${opt.value === 1 ? t("searchPerson") : t("searchPeople")}`,
                    }))}
                    icon={<Users className="w-5 h-5 text-gray-400" />}
                  />
                </div>
              </div>

              {/* Third Row: Room Layout (full width) */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("searchRoomLayout")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {ROOM_LAYOUTS.map((layout) => {
                    const isSelected = roomLayout.includes(layout.value);
                    return (
                      <button
                        key={layout.value}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setRoomLayout(
                              roomLayout.filter((v) => v !== layout.value),
                            );
                          } else {
                            setRoomLayout([...roomLayout, layout.value]);
                          }
                        }}
                        className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors ${
                          isSelected
                            ? "bg-red-600 border-red-600 text-white"
                            : "bg-white border-gray-300 text-gray-700 hover:border-red-300 hover:bg-red-50"
                        }`}
                      >
                        {layout.value}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Search Button */}
              <button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl text-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Search className="w-5 h-5" />
                {t("searchButton")}
              </button>
            </form>
          </div>

          {/* Features Grid */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl 2xl:max-w-6xl 3xl:max-w-7xl mx-auto">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Home className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("featureQualityTitle")}
              </h3>
              <p className="text-gray-600">{t("featureQualityDesc")}</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("featureTrustedTitle")}
              </h3>
              <p className="text-gray-600">{t("featureTrustedDesc")}</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <HeartHandshake className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("featureSupportTitle")}
              </h3>
              <p className="text-gray-600">{t("featureSupportDesc")}</p>
            </div>
          </div>

          {/* Philosophy Section */}
          <div className="mt-20 max-w-5xl 2xl:max-w-6xl 3xl:max-w-7xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sm:p-10 2xl:p-12">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-4">
                  <Sparkles className="w-7 h-7 text-red-600" />
                </div>
                <h2 className="text-2xl sm:text-3xl 2xl:text-4xl font-bold text-gray-900 mb-3">
                  {t("philosophyTitle")}
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                  {t("philosophySubtitle")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-6 2xl:gap-8">
                {/* No Hidden Fees */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-red-50/50 transition-colors">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <Eye className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {t("philosophyNoFeesTitle")}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t("philosophyNoFeesDesc")}
                    </p>
                  </div>
                </div>

                {/* Simple Interface */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-red-50/50 transition-colors">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <Sparkles className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {t("philosophySimpleTitle")}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t("philosophySimpleDesc")}
                    </p>
                  </div>
                </div>

                {/* Quality Listings */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-red-50/50 transition-colors">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <FileText className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {t("philosophyQualityTitle")}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t("philosophyQualityDesc")}
                    </p>
                  </div>
                </div>

                {/* Verified Realtors */}
                <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-red-50/50 transition-colors">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <BadgeCheck className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {t("philosophyVerifiedTitle")}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t("philosophyVerifiedDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-16 max-w-2xl mx-auto">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <p className="text-amber-800 text-sm">
                <span className="font-semibold">🚧 {t("disclaimerTitle")}</span>
                <br />
                {t("disclaimerBody")}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200 relative z-10">
        <div className="max-w-7xl 2xl:max-w-[1600px] 3xl:max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} Apaato. {t("footerRights")}
        </div>
      </footer>
    </div>
  );
}
